import { useEffect, useState } from 'react';

// Date picker built from three plain <select>s (Month / Day / Year).
// We avoid <input type="date"> for the same reason we avoid
// <input type="time"> — the native picker silently drops the value on
// mobile Safari and Samsung Internet when dismissed by swipe instead of
// "Done", which causes "I picked a date but it says I didn't" reports
// from event hosts.
//
// `value` and `onChange` use the canonical ISO `YYYY-MM-DD` string the
// backend already stores. Internal local state keeps the partial
// selection visible mid-entry.
//
// Days available adapt to the chosen month + year (Feb shows 28 or 29
// for leap years, April shows 30, etc.). Past dates are filtered out
// when `minToday` is true so hosts can't accidentally post events for
// yesterday.

const MONTHS = [
  { v: '01', l: 'January' }, { v: '02', l: 'February' },
  { v: '03', l: 'March' },   { v: '04', l: 'April' },
  { v: '05', l: 'May' },     { v: '06', l: 'June' },
  { v: '07', l: 'July' },    { v: '08', l: 'August' },
  { v: '09', l: 'September' },{ v: '10', l: 'October' },
  { v: '11', l: 'November' },{ v: '12', l: 'December' },
];

function daysInMonth(year, month) {
  if (!year || !month) return 31;
  // month is 1-12; passing day=0 to Date returns the last day of the
  // previous month — classic JS trick.
  return new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();
}

function parseISO(str) {
  if (!str || typeof str !== 'string' || !str.includes('-')) return { y: '', m: '', d: '' };
  const [y, m, d] = str.split('-');
  if (!y || !m || !d) return { y: '', m: '', d: '' };
  return { y, m, d };
}

function formatISO(y, m, d) {
  if (!y || !m || !d) return '';
  return `${y}-${m}-${d}`;
}

export default function DatePicker({ value, onChange, testId, minToday = true, yearsAhead = 2 }) {
  const today = new Date();
  const todayY = today.getFullYear();
  const todayM = today.getMonth() + 1;
  const todayD = today.getDate();

  const [y, setY] = useState(() => parseISO(value).y);
  const [m, setM] = useState(() => parseISO(value).m);
  const [d, setD] = useState(() => parseISO(value).d);

  useEffect(() => {
    const incoming = parseISO(value);
    const myFormatted = formatISO(y, m, d);
    if (value !== myFormatted) {
      setY(incoming.y);
      setM(incoming.m);
      setD(incoming.d);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (ny, nm, nd) => {
    setY(ny);
    setM(nm);
    setD(nd);
    if (ny && nm && nd) {
      onChange(formatISO(ny, nm, nd));
    } else {
      onChange('');
    }
  };

  const years = [];
  for (let i = 0; i <= yearsAhead; i++) years.push(String(todayY + i));

  // Filter day options. If the picked month/year is the current calendar
  // month, hide past days when minToday is on. If a future month, show
  // all days. If a past month (only possible when minToday is off), show
  // all days.
  const totalDays = daysInMonth(y, m);
  const dayOptions = [];
  for (let i = 1; i <= totalDays; i++) {
    if (minToday && y === String(todayY) && m === String(todayM).padStart(2, '0') && i < todayD) continue;
    dayOptions.push(String(i).padStart(2, '0'));
  }

  // If the previously-picked day no longer fits (e.g. user changed
  // March 31 → February → no 31), clear the day so the user has to
  // re-pick. This mirrors how native pickers behave.
  useEffect(() => {
    if (d && !dayOptions.includes(d)) {
      commit(y, m, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m, y]);

  // Same idea for month — if user picks current year and a past month,
  // clear it (only possible when minToday is on).
  const monthOptions = MONTHS.filter(opt => {
    if (!minToday) return true;
    if (y && y === String(todayY) && parseInt(opt.v, 10) < todayM) return false;
    return true;
  });

  const baseSelect = "h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#56c6df]";

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <select
        value={m}
        onChange={e => commit(y, e.target.value, d)}
        className={`${baseSelect} flex-1 min-w-[7rem]`}
        data-testid={testId ? `${testId}-month` : undefined}
        aria-label="Month"
      >
        <option value="">Month</option>
        {monthOptions.map(opt => <option key={opt.v} value={opt.v}>{opt.l}</option>)}
      </select>
      <select
        value={d}
        onChange={e => commit(y, m, e.target.value)}
        className={baseSelect}
        data-testid={testId ? `${testId}-day` : undefined}
        aria-label="Day"
        disabled={!m || !y}
      >
        <option value="">Day</option>
        {dayOptions.map(opt => <option key={opt} value={opt}>{parseInt(opt, 10)}</option>)}
      </select>
      <select
        value={y}
        onChange={e => commit(e.target.value, m, d)}
        className={baseSelect}
        data-testid={testId ? `${testId}-year` : undefined}
        aria-label="Year"
      >
        <option value="">Year</option>
        {years.map(yr => <option key={yr} value={yr}>{yr}</option>)}
      </select>
    </div>
  );
}
